<template>
    <div>
        <div class="section-divider">
            <span class="section-title">Token Holdings</span>
        </div>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-2">Loading holdings...</p>
        </div>

        <div v-else-if="error" class="compass-info-box compass-info-box--danger">
            <p class="mb-0">{{ error }}</p>
        </div>

        <div v-else-if="holdings.length === 0" class="compass-info-box">
            <p class="mb-0">No token holdings found for this wallet.</p>
        </div>

        <div v-else>
            <!-- Summary row -->
            <div class="analytics-row cols-3 mb-4" v-if="totalValueUsd > 0">
                <div class="compass-stat-card compass-stat-card--compact">
                    <div class="stat-label">Total Value</div>
                    <div class="stat-value">${{ formatUSD(totalValueUsd) }}</div>
                </div>
                <div class="compass-stat-card compass-stat-card--compact">
                    <div class="stat-label">Tokens</div>
                    <div class="stat-value">{{ pagination.total }}</div>
                </div>
                <div class="compass-stat-card compass-stat-card--compact">
                    <div class="stat-label">Top Holding</div>
                    <div class="stat-value">{{ topHolding }}</div>
                </div>
            </div>

            <div class="table-mobile-scroll">
                <table class="compass-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Token</th>
                            <th class="td-num">Balance</th>
                            <th class="td-num">Price</th>
                            <th class="td-num">Value</th>
                            <th class="td-num">24h Change</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(h, idx) in holdings" :key="h.mint">
                            <td class="col-rank">{{ idx + 1 + pagination.offset }}</td>
                            <td>
                                <div class="d-flex align-items-center gap-2">
                                    <img
                                        v-if="h.image"
                                        :src="h.image"
                                        :alt="h.symbol"
                                        class="rounded-circle"
                                        width="28"
                                        height="28"
                                        loading="lazy"
                                        @error="$event.target.style.display='none'"
                                    >
                                    <div>
                                        <a :href="`/tokens/${h.mint}`" class="fw-semibold text-decoration-none">
                                            {{ h.symbol || formatPubkey(h.mint) }}
                                        </a>
                                        <div class="text-muted small" v-if="h.name">{{ truncate(h.name, 30) }}</div>
                                    </div>
                                </div>
                            </td>
                            <td class="td-num mono-num">{{ formatBalance(h.balance, h.decimals) }}</td>
                            <td class="td-num mono-num">{{ formatPrice(h.price_usd) }}</td>
                            <td class="td-num mono-num fw-semibold">{{ h.value_usd > 0 ? '$' + formatUSD(h.value_usd) : '-' }}</td>
                            <td class="td-num">
                                <span
                                    v-if="h.change_24h_percent !== null"
                                    :class="['pct-change', h.change_24h_percent >= 0 ? 'pct-positive' : 'pct-negative']"
                                >
                                    {{ h.change_24h_percent >= 0 ? '+' : '' }}{{ h.change_24h_percent.toFixed(2) }}%
                                </span>
                                <span v-else class="text-muted">-</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.has_more || pagination.offset > 0">
                <button
                    class="compass-btn compass-btn--sm"
                    :disabled="pagination.offset === 0"
                    @click="loadPage(pagination.offset - pagination.limit)"
                >Previous</button>
                <span class="text-muted small">
                    Showing {{ pagination.offset + 1 }}-{{ Math.min(pagination.offset + holdings.length, pagination.total) }}
                    of {{ pagination.total }}
                </span>
                <button
                    class="compass-btn compass-btn--sm"
                    :disabled="!pagination.has_more"
                    @click="loadPage(pagination.offset + pagination.limit)"
                >Next</button>
            </div>
        </div>
    </div>
</template>

<script>
import { formatCompact, formatPubkey } from '../composables/useFormatters';

export default {
    name: 'WalletHoldings',
    props: {
        address: { type: String, required: true },
    },
    emits: ['loaded'],
    data() {
        return {
            holdings: [],
            pagination: { offset: 0, limit: 50, total: 0, has_more: false },
            loading: true,
            error: null,
        };
    },
    computed: {
        totalValueUsd() {
            return this.holdings.reduce((sum, h) => sum + (h.value_usd || 0), 0);
        },
        topHolding() {
            if (this.holdings.length === 0) return '-';
            return this.holdings[0].symbol || formatPubkey(this.holdings[0].mint);
        },
    },
    methods: {
        formatPubkey,
        formatUSD(value) {
            if (!value) return '0';
            if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
            if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
            if (value >= 1e3) return (value / 1e3).toFixed(2) + 'K';
            return value.toFixed(2);
        },
        formatBalance(balance, decimals) {
            if (!balance) return '0';
            if (balance >= 1e9) return (balance / 1e9).toFixed(2) + 'B';
            if (balance >= 1e6) return (balance / 1e6).toFixed(2) + 'M';
            if (balance >= 1e3) return (balance / 1e3).toFixed(1) + 'K';
            if (balance < 0.0001) return balance.toExponential(2);
            return balance.toLocaleString(undefined, { maximumFractionDigits: 4 });
        },
        formatPrice(price) {
            if (!price || price === 0) return '-';
            if (price >= 1) return '$' + price.toFixed(2);
            if (price >= 0.01) return '$' + price.toFixed(4);
            if (price >= 0.0001) return '$' + price.toFixed(6);
            return '$' + price.toExponential(2);
        },
        truncate(str, len) {
            if (!str || str.length <= len) return str;
            return str.slice(0, len) + '...';
        },
        async fetchHoldings(offset = 0) {
            this.loading = true;
            this.error = null;
            try {
                const resp = await fetch(`/api/v1/wallets/${this.address}/holdings?limit=50&offset=${offset}`);
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const json = await resp.json();
                const data = json.data || json;
                this.holdings = data.holdings || [];
                this.pagination = data.pagination || { offset: 0, limit: 50, total: 0, has_more: false };

                this.$emit('loaded', {
                    total: this.pagination.total,
                    totalValueUsd: this.holdings.reduce((sum, h) => sum + (h.value_usd || 0), 0),
                    holdings: this.holdings,
                });
            } catch (e) {
                this.error = 'Failed to load holdings. ' + e.message;
            } finally {
                this.loading = false;
            }
        },
        loadPage(offset) {
            this.fetchHoldings(Math.max(0, offset));
        },
    },
    mounted() {
        this.fetchHoldings();
    },
};
</script>
